import "./OfflineInputs.scss";

import { Button, ButtonType, DatePicker, Dropdown, FilterBar, Spinner } from "../../Components";
import { MdDelete, MdEdit, MdSave } from "react-icons/md";
import React, { useMemo } from "react";

import { DateRange } from "../../utils/types";
import { DownloadToggle } from "../../Components/DownloadDropdown/DownloadToggle";
import { EmptyInputs } from "./EmptyInputs";
import { HiOutlinePlus } from "react-icons/hi";
import { OfflineInputTabs } from "@blisspointmedia/bpm-types/dist/OfflineInputs";
import { OrderByOptions } from "./OfflineInputsUtils";
import { SetError } from "../../redux/modals";
import cn from "classnames";
import { format } from "date-fns";

interface InputContainerProps {
  dialogs: React.ReactNode;
  tables: React.ReactNode;
  inputType: keyof typeof OfflineInputTabs;
  updated: { by?: string; at?: string };
  isEmpty: boolean;
  fetching: boolean;
  submitting: boolean;
  editingUnlocked: boolean;
  orderBy?: OrderByOptions;
  timelineDateRange?: DateRange;
  setEditingUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
  onFilter: (filter: any) => void;
  validateAndUpdateTables: () => void;
  setShowAddInputModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDiscardDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setError: SetError;
  download: () => void;
  setOrderBy?: React.Dispatch<React.SetStateAction<OrderByOptions>>;
  setTimelineDateRange?: React.Dispatch<React.SetStateAction<DateRange>>;
}

const InputContainer: React.FC<InputContainerProps> = ({
  dialogs,
  tables,
  inputType,
  updated,
  isEmpty,
  fetching,
  submitting,
  editingUnlocked,
  orderBy,
  timelineDateRange,
  setEditingUnlocked,
  onFilter,
  validateAndUpdateTables,
  setShowAddInputModal,
  setShowDiscardDialog,
  setError,
  download,
  setOrderBy,
  setTimelineDateRange,
}) => {
  const updatedLabel: React.ReactNode = useMemo(() => {
    const { by, at } = updated;

    try {
      const atDate = new Date(at ?? "");
      const atStrFormatted = format(atDate, "MM/dd/yyyy");

      return `Last updated on ${atStrFormatted} by ${by}`;
    } catch (e) {
      // If "at" cannot be parsed as a date, display nothing.
      return null;
    }
  }, [updated]);

  const addMediaLabel: string = useMemo(() => {
    switch (inputType) {
      case OfflineInputTabs.PAID_MEDIA_INPUTS:
        return "Paid Media Input";
      case OfflineInputTabs.NON_PAID_MEDIA_INPUTS:
        return "Non-Paid Media Input";
      case OfflineInputTabs.OUTCOME_DATA:
        return "Outcome Data";
      case OfflineInputTabs.PROMOS:
        return "Promos";
      default:
        return "";
    }
  }, [inputType]);

  return (
    <>
      {dialogs}
      <div className="offlineInputsGroup">
        {fetching && <Spinner size={100} />}
        {!fetching && isEmpty && (
          <EmptyInputs
            inputType={addMediaLabel}
            showAddMediaInputModal={() => setShowAddInputModal(true)}
          />
        )}
        {!fetching && !isEmpty && (
          <>
            <div className="tablesHeader">
              <span className="tablesHeaderUpdatedBy">{updatedLabel}</span>
              <span className="tablesActions">
                {editingUnlocked && inputType !== OfflineInputTabs.PROMOS && (
                  <FilterBar hasAdvanced={false} onFilter={onFilter} options={["date"]} />
                )}
                {inputType === OfflineInputTabs.PROMOS && orderBy && setOrderBy && (
                  <Dropdown
                    design={editingUnlocked ? "secondary" : "primary"}
                    label={"Sort by"}
                    value={orderBy}
                    options={[
                      OrderByOptions.DATE_ASCENDING,
                      OrderByOptions.DATE_DESCENDING,
                      OrderByOptions.ALPHABETICAL,
                    ]}
                    onChange={val => {
                      setOrderBy(val);
                    }}
                  ></Dropdown>
                )}
                {inputType === OfflineInputTabs.PROMOS &&
                  timelineDateRange &&
                  setTimelineDateRange && (
                    <DatePicker
                      light={true}
                      design={editingUnlocked ? "secondary" : "primary"}
                      range={timelineDateRange}
                      onChange={newDates => setTimelineDateRange(newDates)}
                      type={ButtonType.FILLED}
                      removeHeaderText={true}
                    ></DatePicker>
                  )}
                {editingUnlocked ? (
                  <Button
                    type={ButtonType.FILLED}
                    design="secondary"
                    disabled={submitting}
                    onClick={() => setShowAddInputModal(true)}
                  >
                    <HiOutlinePlus />
                    Add {addMediaLabel}
                  </Button>
                ) : (
                  <Button type={ButtonType.FILLED} onClick={() => setEditingUnlocked(true)}>
                    <MdEdit />
                    Edit
                  </Button>
                )}
                <div onClick={download}>
                  <DownloadToggle design={editingUnlocked ? "secondary" : "primary"} />
                </div>
              </span>
            </div>
            <div className={cn("tablesContainer", { locked: !editingUnlocked })}>{tables}</div>
            {editingUnlocked && (
              <div className="tablesFooter">
                <div className="tablesActions">
                  <Button
                    type={ButtonType.FILLED}
                    design="secondary"
                    disabled={submitting}
                    onClick={() => setShowDiscardDialog(true)}
                  >
                    <MdDelete />
                    Discard Edits
                  </Button>
                  <Button
                    type={ButtonType.FILLED}
                    disabled={submitting}
                    onClick={validateAndUpdateTables}
                  >
                    {submitting ? (
                      <Spinner />
                    ) : (
                      <>
                        <MdSave />
                        Commit
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default InputContainer;
